/** Lifetime profile counters define these populations; order export history is not the denominator. */
import * as z from 'zod';
import { insightAnswerMetadataSchema } from './insight-answer.js';
import { insightCardPresentationSchema } from './insight-presentation.js';
import { buildRepeatBuyingPresentation } from './repeat-buying-presentation.js';
import { repeatBuyingMetricDefinition, repeatBuyingMetricsSchema } from './repeat-buying-metrics.js';
export const repeatBuyingDefinition = {
    ...repeatBuyingMetricDefinition,
    presentationCopy: {
        title: 'Repeat buying',
        why: 'Shows how many of your buyers have bought more than once, using lifetime order counts on customer profiles.',
        how: 'The share is repeat buyers divided by known buyers. Both counts use lifetime order counts saved on customer profiles. Customers with no orders, or whose order count isn’t known, are left out. This lifetime figure does not show whether repeat buying is improving.',
        noSync: 'Update time unavailable',
        zeroBuyers: 'No known buyers yet',
        zeroBuyersExplanation: 'The percentage needs at least one known buyer.',
        unknownProfiles: 'Profiles with unknown order counts may include buyers. They remain outside the buyer counts and share shown here.',
        profileLabel: 'Customer profiles',
        buyerLabel: 'Bought at least once',
        repeatLabel: 'Bought more than once',
        shareLabel: 'of known buyers bought more than once',
        buyerUnit: 'buyers',
        knownProfiles: 'Profiles with a known lifetime order count.',
        unknownLabel: 'Profiles with unknown order counts',
        sourceLabel: 'Customer profiles',
    },
};
const metricDefinitions = repeatBuyingDefinition.metrics;
const definition = z.strictObject({
    id: z.literal(repeatBuyingDefinition.id),
});
const definitionDescriptor = z.strictObject({
    owner: z.literal(repeatBuyingDefinition.owner),
    reviewReference: z.literal(repeatBuyingDefinition.reviewReference),
    question: z.literal(repeatBuyingDefinition.question),
    population: z.literal(repeatBuyingDefinition.population),
    timeBasis: z.literal(repeatBuyingDefinition.timeBasis),
    missingness: z.literal(repeatBuyingDefinition.missingness),
    counterDomain: z.literal(repeatBuyingDefinition.counterDomain),
    metrics: z.strictObject({
        totalProfiles: z.strictObject({
            id: z.literal(metricDefinitions.totalProfiles.id),
            unit: z.literal(metricDefinitions.totalProfiles.unit),
            population: z.literal(metricDefinitions.totalProfiles.population),
        }),
        zeroOrderProfiles: z.strictObject({
            id: z.literal(metricDefinitions.zeroOrderProfiles.id),
            unit: z.literal(metricDefinitions.zeroOrderProfiles.unit),
            population: z.literal(metricDefinitions.zeroOrderProfiles.population),
        }),
        oneTimeBuyers: z.strictObject({
            id: z.literal(metricDefinitions.oneTimeBuyers.id),
            unit: z.literal(metricDefinitions.oneTimeBuyers.unit),
            population: z.literal(metricDefinitions.oneTimeBuyers.population),
        }),
        repeatBuyers: z.strictObject({
            id: z.literal(metricDefinitions.repeatBuyers.id),
            unit: z.literal(metricDefinitions.repeatBuyers.unit),
            population: z.literal(metricDefinitions.repeatBuyers.population),
        }),
        unknownOrderProfiles: z.strictObject({
            id: z.literal(metricDefinitions.unknownOrderProfiles.id),
            unit: z.literal(metricDefinitions.unknownOrderProfiles.unit),
            population: z.literal(metricDefinitions.unknownOrderProfiles.population),
        }),
        knownBuyers: z.strictObject({
            id: z.literal(metricDefinitions.knownBuyers.id),
            unit: z.literal(metricDefinitions.knownBuyers.unit),
            population: z.literal(metricDefinitions.knownBuyers.population),
            formula: z.strictObject({ addends: z.tuple([z.literal('oneTimeBuyers'), z.literal('repeatBuyers')]) }),
        }),
        repeatShare: z.strictObject({
            id: z.literal(metricDefinitions.repeatShare.id),
            unit: z.literal(metricDefinitions.repeatShare.unit),
            population: z.literal(metricDefinitions.repeatShare.population),
            formula: z.strictObject({ numerator: z.literal('repeatBuyers'), denominator: z.literal('knownBuyers') }),
            exclusions: z.tuple([z.literal('zeroOrderProfiles'), z.literal('unknownOrderProfiles')]),
            zeroBase: z.literal('unavailable'),
            zeroNumeratorWithPositiveBase: z.literal(0),
        }),
    }),
    acceptedInputs: z.tuple([]),
    acceptedBreakdowns: z.tuple([]),
    unsupportedClaims: z.tuple([
        z.literal(repeatBuyingDefinition.unsupportedClaims[0]),
        z.literal(repeatBuyingDefinition.unsupportedClaims[1]),
        z.literal(repeatBuyingDefinition.unsupportedClaims[2]),
    ]),
});
export const repeatBuyingAnswerSchema = repeatBuyingMetricsSchema
    .safeExtend({
    ...insightAnswerMetadataSchema.shape,
    state: z.literal('ready'),
    definition,
    descriptor: definitionDescriptor,
    evidence: insightAnswerMetadataSchema.shape.evidence.extend({
        basis: z.literal(repeatBuyingDefinition.basis),
        sourceTimestamps: z.tuple([
            z.strictObject({ source: z.literal('customers'), syncedAt: z.iso.datetime({ offset: true }).nullable() }),
        ]),
    }),
    actions: z.array(z.never()).max(0),
    presentation: insightCardPresentationSchema,
})
    .superRefine((r, ctx) => {
    if (JSON.stringify(r.presentation) !== JSON.stringify(buildRepeatBuyingPresentation(r)))
        ctx.addIssue({
            code: 'custom',
            message: 'INSIGHT_PRESENTATION_INVALID: repeat card projection differs from reviewed canonical facts',
        });
});
//# sourceMappingURL=repeat-buying-answer.js.map